import React from 'react'
import type { TicketStatus, TicketChannel } from '../../utils/api'

/**
 * Small inline badges for the tickets feature. Kept in one place so the list
 * and detail pages render statuses identically. Follows the dashboard's
 * existing pill conventions (rounded-md, text-[11px], font-semibold, border).
 */

const pill =
  'inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold border leading-tight'

const STATUS_STYLES: Record<TicketStatus, string> = {
  open: 'bg-success-light text-green-700 dark:text-green-300 border-green-200 dark:border-green-800',
  answered:
    'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 border-blue-200 dark:border-blue-800',
  closed: 'bg-surface-tertiary text-txt-secondary border-border',
}

export const TicketStatusBadge: React.FC<{ value: TicketStatus | string }> = ({ value }) => {
  const style =
    STATUS_STYLES[value as TicketStatus] || 'bg-surface-tertiary text-txt-secondary border-border'
  return (
    <span className={`${pill} ${style}`}>
      {String(value).charAt(0).toUpperCase() + String(value).slice(1)}
    </span>
  )
}

const CHANNEL_LABELS: Record<TicketChannel, string> = {
  chat: 'Chat',
  email: 'Email',
}

const CHANNEL_STYLES: Record<TicketChannel, string> = {
  chat: 'bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 border-purple-200 dark:border-purple-800',
  email:
    'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 border-amber-200 dark:border-amber-800',
}

const ChatIcon = () => (
  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z"
    />
  </svg>
)

const EmailIcon = () => (
  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
    />
  </svg>
)

export const TicketChannelBadge: React.FC<{ value: TicketChannel | string }> = ({ value }) => {
  const v = value as TicketChannel
  const label = CHANNEL_LABELS[v] || String(value)
  const style = CHANNEL_STYLES[v] || 'bg-surface-tertiary text-txt-secondary border-border'
  return (
    <span className={`${pill} ${style}`}>
      {v === 'email' ? <EmailIcon /> : <ChatIcon />}
      {label}
    </span>
  )
}
